/**
 * Color Picker Component
 *
 * Choix d'une couleur libre : une pastille qui ouvre le selecteur natif du
 * systeme, et un champ hexadecimal pour saisir ou coller une valeur precise.
 *
 * Il y avait auparavant une palette fermee de seize couleurs. Elle ne permet
 * pas de reproduire une charte : un exploitant qui compose un panneau de menu
 * ou qui range ses ecrans par couleur a une valeur precise en tete, pas un
 * echantillon approchant (retour utilisateur du 2026-09-23). Quelques
 * raccourcis restent proposes quand l'appelant en fournit, mais ils ne sont
 * plus le seul moyen de choisir.
 *
 * `allowInherit` (heriter/personnalisee) se peint via `SegmentedControl`
 * (pas deux boutons radio) : les libelles fournis par les appelants (ex
 * "Blanc (par defaut)", "Aucun (transparent)") cassaient sur plusieurs lignes
 * dans une colonne de proprietes etroite (retour utilisateur du 2026-09-24) —
 * un groupe segmente tient sur une seule ligne, tronque proprement au besoin
 * (infobulle native du bouton pour le libelle complet).
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { SegmentedControl } from './SegmentedControl';

/** Raccourcis proposes par defaut sous le champ. */
export const DEFAULT_COLORS = [
  '#D32F2F', // Rouge
  '#E91E63', // Rose
  '#9C27B0', // Violet
  '#3F51B5', // Indigo
  '#2196F3', // Bleu
  '#009688', // Teal
  '#4CAF50', // Vert
  '#FFC107', // Jaune
  '#FF9800', // Orange
  '#795548', // Marron
  '#607D8B', // Gris bleu
  '#111111', // Noir
  '#FFFFFF', // Blanc
];

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Normalise une saisie libre en `#RRGGBB`, ou `null` si elle n'est pas exploitable. */
function normalizeHex(raw: string): string | null {
  const value = raw.trim().replace(/^#?/, '#');
  if (HEX_PATTERN.test(value)) return value.toUpperCase();
  // Forme courte `#abc`, couramment collee depuis une charte graphique.
  if (/^#[0-9a-fA-F]{3}$/.test(value)) {
    const [r, g, b] = value.slice(1);
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

export interface ColorPickerProps {
  /** Couleur courante (`null` = heritee / valeur par defaut). */
  value: string | null | undefined;
  /** Appelee a chaque changement de couleur. */
  onChange: (color: string | null) => void;
  /** Raccourcis affiches sous le champ ; `[]` pour n'en afficher aucun. */
  colors?: string[];
  /** Propose le choix « heriter » / « personnalisee ». */
  allowInherit?: boolean;
  /** Libelle de l'option « heriter ». */
  inheritLabel?: string;
  /** Libelle de l'option « couleur choisie ». */
  customLabel?: string;
  /** Couleur du parent, montree a cote de l'option « heriter ». */
  parentColor?: string | null;
  /** Conserve pour compatibilite d'appel ; sans effet depuis le passage en couleur libre. */
  columns?: number;
  className?: string;
}

export function ColorPicker({
  value,
  onChange,
  colors = DEFAULT_COLORS,
  allowInherit = false,
  inheritLabel = 'Heriter du parent',
  customLabel = 'Personnalisee',
  parentColor,
  className,
}: ColorPickerProps) {
  const isInheriting = allowInherit && !value;
  const current = value ?? parentColor ?? '#FFFFFF';

  // Saisie libre : on laisse l'utilisateur taper une valeur incomplete sans
  // la corriger a chaque frappe, et on ne remonte le changement qu'une fois
  // la valeur exploitable.
  const [draft, setDraft] = useState(current);
  useEffect(() => setDraft(current), [current]);

  const handleDraftChange = (raw: string) => {
    setDraft(raw);
    const normalized = normalizeHex(raw);
    if (normalized) onChange(normalized);
  };

  /*
   * Glisser dans le selecteur natif (`<input type="color">`) declenche
   * l'evenement DOM 'input' en continu, des dizaines de fois par seconde —
   * et React expose CET evenement (pas 'change') sur la prop `onChange` d'un
   * input controle. Le repercuter a chaque tick jusqu'au parent (souvent un
   * etat de page entier, cf editeur de panneaux BDisplay) provoquait un
   * nouveau rendu complet a chaque fraction de pixel glissee — la pastille
   * elle-meme restait fluide (son propre `draft`, local et gratuit), mais
   * tout ce qui en dependait ailleurs dans l'appli ramait pendant tout le
   * geste (retour utilisateur du 2026-09-24). On separe donc les deux :
   * l'apercu local (`draft`, la pastille + le champ hexadecimal) suit chaque
   * tick, mais la valeur n'est remontee au parent (`onChange`) qu'une seule
   * fois, a la fin du geste — l'evenement natif 'change' (pas expose par
   * React sur ce type d'input), capte ici via un listener DOM manuel.
   */
  const colorInputRef = useRef<HTMLInputElement>(null);
  // Toujours la derniere version de `onChange`, sans reposer l'ecouteur.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Rattache a `isInheriting`, pas a `onChange` : le selecteur natif n'existe
  // pas en mode « heriter » et n'apparait qu'au passage en « personnalisee ».
  // Rattache a `onChange` seul, l'ecouteur n'etait jamais pose quand
  // l'appelant passe une fonction stable (`setState`) — cas du fond de
  // panneau BDisplay, qui restait blanc quoi qu'on choisisse (retour
  // utilisateur du 2026-09-30).
  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;
    const commit = () => onChangeRef.current(normalizeHex(el.value) ?? el.value.toUpperCase());
    el.addEventListener('change', commit);
    return () => el.removeEventListener('change', commit);
  }, [isInheriting]);

  return (
    <div className={cn('space-y-2', className)}>
      {allowInherit && (
        <div className="flex min-w-0 items-center gap-2">
          <SegmentedControl
            className="min-w-0 flex-1"
            ariaLabel="Origine de la couleur"
            value={isInheriting ? 'inherit' : 'custom'}
            onChange={(v) => onChange(v === 'inherit' ? null : (normalizeHex(draft) ?? current))}
            options={[
              { value: 'inherit', label: inheritLabel },
              { value: 'custom', label: customLabel },
            ]}
          />
          {isInheriting && parentColor && (
            <span className="h-3 w-3 shrink-0 rounded-full border border-neutral-300" style={{ backgroundColor: parentColor }} title={parentColor} />
          )}
        </div>
      )}

      {!isInheriting && (
        <>
          <div className="flex items-center gap-2">
            {/* La pastille EST le selecteur natif : un clic dessus ouvre celui
                du systeme, avec pipette et nuancier complets. L'input est
                rendu invisible mais reste focalisable au clavier. */}
            <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-neutral-300 shadow-sm">
              <span className="absolute inset-0" style={{ backgroundColor: normalizeHex(draft) ?? current }} />
              <input
                ref={colorInputRef}
                type="color"
                value={normalizeHex(draft) ?? current}
                onChange={(e) => setDraft(e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="Choisir une couleur"
              />
            </span>

            <input
              type="text"
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onBlur={() => setDraft(normalizeHex(draft) ?? current)}
              spellCheck={false}
              placeholder="#RRGGBB"
              aria-label="Code hexadécimal de la couleur"
              className={cn(
                'h-9 w-full min-w-0 flex-1 rounded-lg border px-2 font-mono text-sm uppercase outline-none',
                normalizeHex(draft)
                  ? 'border-neutral-300 text-dark-500 focus:border-primary-400'
                  : 'border-danger-400 text-danger-600'
              )}
            />
          </div>

          {colors.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => onChange(color.toUpperCase())}
                  title={color.toUpperCase()}
                  aria-label={`Couleur ${color.toUpperCase()}`}
                  className={cn(
                    'h-5 w-5 rounded border transition-transform hover:scale-110',
                    (normalizeHex(draft) ?? current) === color.toUpperCase()
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-neutral-300'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
